import { useEffect, useState } from 'react';

/**
 * True once `isReady` has remained `false` for at least `timeoutMs`.
 *
 * Guards against the "Clerk never loads" deadlock: if Clerk.js is blocked in
 * the browser (adblocker, CSP, Cloudflare challenge on the Clerk domain, …),
 * `isLoaded` stays `false` forever. That disables the root loader query and
 * renders the LoadingWidget indefinitely. Instead of an eternal spinner we
 * surface an honest error state.
 *
 * `isReady` is `isLoaded && !isLoaderQueryPending(query)`, i.e. it is `false`
 * in TWO distinct situations:
 *   1. Clerk.js has not finished loading (`isLoaded === false`).
 *   2. Clerk loaded, but the root loader query has not produced data/error —
 *      either because it is disabled (never ran: signed-out user on a
 *      protected route — now redirected to login before this fires) or
 *      because it is genuinely hanging (stale session cookie stalling the
 *      backend loader).
 * The timeout message therefore names the actual observed condition so the
 * two failure modes can be told apart during debugging.
 *
 * Semantics:
 *  - While `isReady === false` a `setTimeout(timeoutMs)` starts.
 *  - If it fires while `isReady` is still `false`, returns `true` and logs a
 *    diagnostic line (no secrets) to help future debugging.
 *  - The moment `isReady` becomes `true` the flag resets to `false`, so a
 *    normal (brief) auth transition keeps showing the spinner and never
 *    shows the error state.
 *
 * Cleanup clears the timer on unmount / re-run, so there is no interval and
 * no infinite loop. React 18 StrictMode double-invocations are harmless: the
 * first effect's timer is cleared by its own cleanup before the second runs.
 *
 * @param isReady Clerk-ready predicate (`isLoaded` AND loader not pending).
 * @param timeoutMs How long to wait before failing (default 10 s)
 */
export function useClerkLoadTimeout(
  isReady: boolean,
  timeoutMs = 10_000,
): boolean {
  const [
    timedOut,
    setTimedOut,
  ] = useState(false);

  useEffect(() => {
    // Ready (Clerk loaded and loader query settled) — reset any pending flag.
    if (isReady) {
      setTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      setTimedOut(true);
      // eslint-disable-next-line no-console
      console.warn(
        `[immodossier-client] Auth did not become ready within ${timeoutMs} ms ` +
          `(isLoaded=${isReady}). If a signed-in user's /loader/ query is ` +
          'disabled/pending this is usually an unresolved Clerk session — ' +
          'the page will be redirected to login. If Clerk itself never ' +
          'loaded, check network / adblocker / Clerk domain.',
      );
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [
    isReady,
    timeoutMs,
  ]);

  return timedOut;
}
